import { useState, useEffect, useCallback, useRef } from 'react';
import { useDealSubscriptions } from '@/creditflowresearch/application/Deal/useDealSubscriptions';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { useDealFilters } from '@/creditflowresearch/application/Deal/useDealFilters';
import { useProductsContext } from '@/creditflowresearch/application/Deal/contexts/ProductsContext';
import { IDealsPayload } from '@/creditflowresearch/domain/vo/IDeal';
import { ICorporatePayload } from '@/creditflowresearch/application/Deal/types';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';

export interface ISubscription {
  subscriptionId: string,
  subscriptionSubgroupName: string,
  subscriptionName: string,
  isSubscribed: boolean,
  subscriptionRequiredPlan: UserPlanType,
}

export interface IDealsCollection<T, P extends IDealsPayload | ICorporatePayload> {
  totalCount: number,
  collection: T[],
  subscriptionStatus?: ISubscription | null,
  pageFilters?: P | null,
}

interface IUseDealsProps<T, P extends IDealsPayload | ICorporatePayload> {
  deals: IDealsCollection<T, P>,
  fetch?: (payload: P) => Promise<IDealsCollection<T, P>>,
  additionalFilters?: P,
  isActive: boolean,
}

const INITIAL_PAGE = 1;
const BUFFER_PER_PAGE = 20;
const LOAD_MORE_PER_PAGE = 10;
const INITIAL_VISIBLE_COUNT = 10;

const useDeals = <T, P extends IDealsPayload | ICorporatePayload>({
  deals,
  fetch,
  additionalFilters,
  isActive,
}: IUseDealsProps<T, P>) => {
  const {
    collection = [],
    totalCount: initialTotalCount,
    subscriptionStatus,
    pageFilters,
  } = deals;

  const { buildFilters } = useDealFilters<P>({ additionalFilters, pageFilters });
  const { selectedTab } = useProductsContext();

  const tabId = selectedTab?.id;

  const [dealsCollection, setDealsCollection] = useState<T[]>(collection.slice(0, INITIAL_VISIBLE_COUNT));
  const [bufferCollection, setBufferCollection] = useState<T[]>(collection.slice(INITIAL_VISIBLE_COUNT));
  const [totalCount, setTotalCount] = useState<number>(initialTotalCount);
  const [isFetching, setIsFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(INITIAL_PAGE + 1);
  const [currentSubscriptionStatus, setCurrentSubscriptionStatus] = useState(subscriptionStatus);

  const {
    subscribeSuccessMessage,
    subscribeButtonText,
    subscriptions,
    markSubscribed,
  } = useDealSubscriptions(currentSubscriptionStatus);
  const isFirstMountRef = useRef<boolean>(true);

  const hasBufferedItems = bufferCollection.length > 0;

  const fetchDeals = useCallback(
    async (payloadToFetch: P) => {
      if (!fetch) {
        return;
      }

      try {
        setIsFetching(true);
        const response = await fetch(payloadToFetch);
        setTotalCount(response?.totalCount);

        if (response?.subscriptionStatus) {
          setCurrentSubscriptionStatus(response.subscriptionStatus);
        }

        if (payloadToFetch.page === INITIAL_PAGE) {
          setDealsCollection(response?.collection.slice(0, INITIAL_VISIBLE_COUNT));
          setBufferCollection(response?.collection.slice(INITIAL_VISIBLE_COUNT));
          setCurrentPage(INITIAL_PAGE + 1);
        } else {
          setDealsCollection((prev) => [...prev, ...response.collection]);
          setCurrentPage(payloadToFetch.page);
        }
      } catch (error) {
        AlertManager.error(getErrorMessage(error));
      } finally {
        setIsFetching(false);
      }
    },
    [fetch],
  );

  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }

    if (!isActive) {
      return;
    }

    const filters = buildFilters();
    const payload: P = {
      ...pageFilters,
      filters,
      filterType: pageFilters?.filterType,
      page: INITIAL_PAGE,
      perPage: BUFFER_PER_PAGE,
    } as P;

    fetchDeals(payload);
  }, [isActive, JSON.stringify(buildFilters()), tabId, fetchDeals]);

  const loadMore = useCallback(() => {
    if (hasBufferedItems) {
      setDealsCollection((prev) => [...prev, ...bufferCollection]);
      setBufferCollection([]);
      return;
    }

    const nextPage = currentPage + 1;
    const filters = buildFilters();
    const payload: P = {
      ...pageFilters,
      filters,
      perPage: LOAD_MORE_PER_PAGE,
      page: nextPage,
    } as P;

    fetchDeals(payload);
  }, [hasBufferedItems, bufferCollection, currentPage, buildFilters, pageFilters, fetchDeals]);

  return {
    subscriptions,
    isFetching,
    collection: dealsCollection,
    totalCount,
    subscribeSuccessMessage,
    subscribeButtonText,
    markSubscribed,
    loadMore,
    fetchDeals,
    pageFilters,
  };
};

export default useDeals;
